import React, { useContext, useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import classNames from "classnames";
import { useSelector } from "react-redux";
import { H1 } from "components/core/Typography";
import { FilterIconSolid, MapPinOutline } from "components/core/Icons";
import "./StickToolBar.scss";
import { Button } from "components/core/Button";
import ClickToCall from "components/brand/ClickToCall/ClickToCall";
import useTollfreeNumber from "hooks/useTollfreeNumber";
import useGetCurrentLocation from "hooks/useGetCurrentLocation";
import SearchPageContext from "pages/SearchPageV1/SearchPageV1Context";
import { FiltersCard } from "components/organisms/search/Filters";
import SearchBar from "../SearchBar/SearchBar";

function StickToolBar({
	headingTitle = "",
	filterCount = 0,
	location = "",
	isNMP = false,
	city = "",
	hasCoords = false,
	onLocate
}) {
	const [isScrolling, setIsScrolling] = useState(false);
	const toolbarRef = useRef(null);

	const {
		isCallCenterOpen,
		showInlineFilters,
		setShowInlineFilters,
		searchFilters,
		handleFilterChange,
		applySearch,
		filtersContainerRef
	} = useContext(SearchPageContext);
	const { phone, handleFetchNumber } = useTollfreeNumber();
	const geolocationSource = useSelector(
		(state) => state.user?.geolocationSource
	);
	const { fetchLocation, isLoading, error } = useGetCurrentLocation({
		onSuccess: onLocate
	});
	const isBrowser = hasCoords || geolocationSource === "browser";

	useEffect(() => {
		const handleScroll = () => {
			if (globalThis.window?.scrollY > 0) {
				setIsScrolling(true);
			} else {
				setIsScrolling(false);
			}
		};

		globalThis.window?.addEventListener("scroll", handleScroll);
		return () =>
			globalThis.window?.removeEventListener("scroll", handleScroll);
	}, []);

	useEffect(() => {
		const toolbarEl = toolbarRef.current;
		const root = globalThis.document?.documentElement;
		if (!toolbarEl || !root || typeof ResizeObserver === "undefined") {
			return undefined;
		}

		const updateHeightVar = () => {
			root.style.setProperty(
				"--stick-toolbar-height",
				`${toolbarEl.offsetHeight}px`
			);
		};

		const observer = new ResizeObserver(updateHeightVar);
		observer.observe(toolbarEl);
		updateHeightVar();

		return () => {
			observer.disconnect();
			root.style.removeProperty("--stick-toolbar-height");
		};
	}, []);

	const locationBtnLabel = isBrowser
		? "Update Location"
		: "Get Current Location";
	let locationNoticeText;
	if (isBrowser) {
		locationNoticeText = city
			? `Your location has been set to ${city}.`
			: "Your location has been set.";
	} else {
		locationNoticeText = city
			? `It looks like you're in ${city}. Not correct?`
			: "Find storage near you.";
	}

	return (
		<div
			ref={toolbarRef}
			className={classNames("stick-toolbar", {
				"stick-toolbar--fixed": isScrolling
			})}
		>
			<div className="stick-toolbar__title-group">
				<H1
					className="heading"
					color="white"
					weight="bold"
					size="small"
					data-testid="heading"
				>
					{headingTitle}
				</H1>
				{isNMP && (
					<div
						className={classNames(
							"stick-toolbar__location-banner stick-toolbar__location-banner--desktop",
							{ "stick-toolbar__location-banner--error": error }
						)}
						data-testid="location-banner"
					>
						{error ? (
							<strong
								className="stick-toolbar__location-notice"
								data-testid="location-notice"
							>
								We could not determine your precise location.
								Please enable location in your browser settings.
							</strong>
						) : (
							<span className="stick-toolbar__location-notice">
								{locationNoticeText}
							</span>
						)}
						<Button
							type="button"
							theme="text"
							fullWidth={false}
							className="stick-toolbar__location-btn"
							onClick={fetchLocation}
							disabled={isLoading}
						>
							<MapPinOutline aria-hidden />
							{isLoading ? "Locating..." : locationBtnLabel}
						</Button>
					</div>
				)}
			</div>
			<div className="searchbar-container">
				<div
					className="help-filter-btn"
					data-testid="callContainer"
				>
					{!!isCallCenterOpen && (
						<div>
							<ClickToCall
								className="tray-button tray-button--call"
								color="white"
								showIcon
								underline={false}
								phone={phone}
								handleFetchNumber={handleFetchNumber}
								iconSize="large"
							/>
						</div>
					)}
					<div
						className="stick-toolbar__filters"
						ref={filtersContainerRef}
					>
						<Button
							type="button"
							className={classNames("filter-button", {
								"is-active": !!showInlineFilters
							})}
							aria-haspopup="dialog"
							aria-expanded={!!showInlineFilters}
							aria-pressed={showInlineFilters ? "true" : "false"}
							onClick={() => {
								if (
									typeof setShowInlineFilters === "function"
								) {
									setShowInlineFilters(!showInlineFilters);
								}
							}}
						>
							<FilterIconSolid
								size="large"
								fill="white"
								aria-hidden
							/>
							<span className="filter-btn-label">Filters</span>
							{filterCount > 0 && (
								<div className="filter-btn-count">
									<span
										className="filter-btn-num"
										aria-label={`Applied filters: ${filterCount}`}
									>
										{filterCount}
									</span>
								</div>
							)}
						</Button>
						{searchFilters && showInlineFilters && (
							<div
								className="stick-toolbar__filters-popover"
								role="dialog"
								aria-label="Filters"
							>
								<FiltersCard
									onFilterChange={handleFilterChange}
									activeFilters={searchFilters}
									onClose={() => {
										if (
											typeof setShowInlineFilters ===
											"function"
										) {
											setShowInlineFilters(false);
										}
									}}
									onApply={() => {
										if (typeof applySearch === "function") {
											applySearch();
										}
										if (
											typeof setShowInlineFilters ===
											"function"
										) {
											setShowInlineFilters(false);
										}
									}}
								/>
							</div>
						)}
					</div>
				</div>
				<SearchBar
					buttonText="Find Storage"
					id="hero-search-bar"
					label="Search by city, state, or zip"
					theme="darkBlue"
					showLabel={false}
					className="searchbar"
					location={location}
				/>
			</div>
			{isNMP && (
				<div
					className={classNames(
						"stick-toolbar__location-banner stick-toolbar__location-banner--mobile",
						{ "stick-toolbar__location-banner--error": error }
					)}
					data-testid="location-banner-mobile"
				>
					{error ? (
						<strong className="stick-toolbar__location-notice">
							We could not determine your precise location. Please
							enable location in your browser settings.
						</strong>
					) : (
						<span className="stick-toolbar__location-notice">
							{locationNoticeText}
						</span>
					)}
					<Button
						type="button"
						theme="text"
						fullWidth={false}
						className="stick-toolbar__location-btn"
						onClick={fetchLocation}
						disabled={isLoading}
						data-testid="location-btn-mobile"
					>
						<MapPinOutline aria-hidden />
						{isLoading ? "Locating..." : locationBtnLabel}
					</Button>
				</div>
			)}
		</div>
	);
}

StickToolBar.propTypes = {
	headingTitle: PropTypes.string,
	filterCount: PropTypes.number,
	location: PropTypes.string,
	isNMP: PropTypes.bool,
	city: PropTypes.string,
	hasCoords: PropTypes.bool,
	onLocate: PropTypes.func
};

export default StickToolBar;
